import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginStart, loginSuccess, loginFailure, selectUser } from '../../store/slices/authSlice';
import { store } from '../../store';
import { authAPI } from '../../services/auth.api';

// Import fonts
import '@fontsource/oswald/700.css'; // Bold variant
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';

// Material UI components
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,     
  Grid,
  FormControlLabel,
  Checkbox,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  styled
} from '@mui/material';
import { Visibility, VisibilityOff, RestaurantMenu } from '@mui/icons-material';

// Custom styled components
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  '& label': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& label.Mui-focused': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiInputBase-input': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
}));

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Login attempt with:', formData.email);
      const response = await authAPI.login(formData);
      console.log('Login response:', response);
      console.log('User data:', response.data.user);
      
      // First dispatch the login actions
      dispatch(loginStart());
      dispatch(loginSuccess(response.data));
      
      // Redirect based on user role
      if (response.data.user && response.data.user.role === 'admin') {
        console.log('Admin login detected, redirecting to admin dashboard');
        console.log('Admin user object:', response.data.user);
        
        // Store admin flag in localStorage
        localStorage.setItem('isAdmin', 'true');
        
        // Add a link option for manual navigation
        const linkMsg = document.createElement('div');
        linkMsg.innerHTML = 'If not redirected, <a href="/admin-panel" style="color: blue; text-decoration: underline;">click here to go to admin panel</a>';
        linkMsg.style.position = 'fixed';
        linkMsg.style.top = '10px';
        linkMsg.style.right = '10px';
        linkMsg.style.background = 'white';
        linkMsg.style.padding = '10px';
        linkMsg.style.border = '1px solid black';
        linkMsg.style.zIndex = '9999';
        document.body.appendChild(linkMsg);
        
        // Try direct URL navigation for admin panel
        window.location.href = '/admin-panel';
      } else {
        console.log('User login detected, redirecting to user dashboard');
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.message || 'Login failed';
      dispatch(loginFailure(errorMessage));
      setError(errorMessage);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/backgroundAKB.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'fixed',
        top: 0,
        left: 0
      }}
    >
      <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 2 ,maxWidth: '500px'}}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
        <Box
          sx={{
            p: 3,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'transparent'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img src="/logoAkb.png" alt="Aaj Kya Banega Logo" width="130" />
          </Box>
          
          <Typography component="h1" variant="h5" 
            sx={{ 
              mb: 1, 
              fontFamily: 'Oswald', 
              fontWeight: 700,
              color: 'white',
              fontSize: '28px',
              letterSpacing: '0.5px'
            }}
          >
            Welcome To Aaj Kya Banega
          </Typography>
          
          <Typography component="p" variant="body2" 
            sx={{ 
              // mb: 4, 
              fontFamily: 'Roboto',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px'
            }}
          >
            Enter your email and password to access your account
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
            <StyledTextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              variant="standard"
              // sx={{ mb: 2 }}
            />
            <StyledTextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              variant="standard"
              // sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword}
                      edge="end"
                      sx={{ color: 'white' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <Grid container sx={{ mt: 1 }} justifyContent="space-between">
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      value="remember"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-checked': {
                          color: 'rgba(255, 255, 255, 0.9)',
                        },
                      }}
                      checked={rememberMe}
                      onChange={handleRememberMeChange}
                    />
                  }
                  label="Remember me"
                  sx={{ 
                    fontFamily: 'Roboto',
                    color: 'rgba(255, 255, 255, 0.7)',
                    '& .MuiTypography-root': {
                      fontSize: '14px',
                    }
                  }}
                />
              </Grid>
              <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                {/* <Link href="#" variant="body2" sx={{ fontFamily: 'Roboto', color: '#F9D643', fontSize: '14px', textDecoration: 'none' }}>
                  Forgot password?
                </Link> */}
              </Grid>
            </Grid>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ 
                mt: 2, 
                mb: 2, 
                py: 1.25,
                bgcolor: '#007619',
                '&:hover': {
                  bgcolor: '#007619',
                },
                fontFamily: 'Roboto',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: '500',
                textTransform: 'none',
                letterSpacing: '0.5px'
              }}
            >
              Sign In
            </Button>
            
            <Grid container justifyContent="center">
              <Grid item>
                <Typography variant="body2" align="center" sx={{ fontFamily: 'Roboto', color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                  {/* Don't have an account?{' '} */}
                  {/* <Link component={RouterLink} to="/register" variant="body2" sx={{ color: '#F9D643', fontWeight: 500, textDecoration: 'none' }}>
                    Create one now
                  </Link> */}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Container>
    </Box>
  );
};

export default Login;
